import type { BidAskPair } from '@/types/common.ts';
import { roundToFixed } from '@/utils/number.ts';

export type BidAskRate = BidAskPair;
export type BidAskPoint = BidAskPair;
export type BidAskPPS = BidAskPair;
export type BidAskAmount = BidAskPair;

/**
 * In the FOREX market, a "point" (often used interchangeably with "pip") typically refers to the smallest price increment in
 * a currency pair's exchange rate. For the most major pairs (EUR/USD, GBP/USD, etc.) quoted with four decimals, one point is
 * the fourth decimal place (e.g., from 1.3000 to 1.3001). Some brokers quote an extra digit (the fifth decimal), where
 * traditional pip remains the fourth decimal place, and the fifth decimal represents a fractional pip (0.00001).
 *
 * Example: if the smallest move in the EUR/USD pair is 0.00001 (precision = 5), then one point is 0.00001.
 */

// shift the dot to the left by the number of digits in the precision
// Math.round is used because javascript can make mistakes with floating point numbers. for examples:
// - 3243.2 / 10**5 = 0.032431999999999996
// - Math.round((3243.2 / 10**5) * 10**5)/10**5 = 0.03243
/**
 * Converts points to rate based on given precision.
 *
 * @param points - The number of points to convert.
 * @param precision - The precision level (number of decimal places).
 * @param pointPrecision - The number of decimal of one point; by default, equal to the precision.
 * @returns The converted exchange rate as a number.
 */
export const convertPointsToRate = (points: number, precision: number, pointPrecision?: number): number => {
  const pointScale = 10 ** (pointPrecision ?? precision);

  return roundToFixed(points / pointScale, precision);
};

/**
 * Converts a rate to points based on given precision.
 *
 * @param rate - The exchange rate to convert.
 * @param precision - The precision level (number of decimal places).
 * @returns The converted number of points as an integer.
 */
export const convertRateToPoints = (rate: number, precision: number) => {
  return 10 ** precision * rate;
};

export const convertBidAskRateToBidAskPoints = (rates: BidAskRate, precision: number): BidAskPoint => ({
  bid: convertRateToPoints(rates.bid, precision),
  ask: convertRateToPoints(rates.ask, precision),
});

// 1 PPS = 10 points
export const convertBidAskPointToBidAskPPS = (rates: BidAskPoint): BidAskPPS => ({
  bid: rates.bid / 10,
  ask: rates.ask / 10,
});

export const convertBidAskPPSToBidAskPoints = (rates: BidAskPPS): BidAskPoint => ({
  bid: rates.bid * 10,
  ask: rates.ask * 10,
});

/**
 * Adds two exchange rates with a specified precision and returns the result.
 *
 * @param leftRate - The first exchange rate to add.
 * @param rightRate - The second exchange rate to add.
 * @param precision - The precision level (number of decimal places) for both rates.
 * @returns The sum of the two rates as an exchange rate with the specified precision.
 */
export const addRatesWithPrecision = (leftRate: number, rightRate: number, precision: number) => {
  const leftRatePoints = convertRateToPoints(leftRate, precision);
  const rightRatePoints = convertRateToPoints(rightRate, precision);

  return convertPointsToRate(leftRatePoints + rightRatePoints, precision);
};
